import React, { useState, useEffect } from 'react';
import { ScanLine, RefreshCw, AlertTriangle, X, CheckCircle, Truck, Warehouse, ArrowRight, Package } from 'lucide-react';
import MainService from '../../services/MainService';

const PickupScan = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [voucherNo, setVoucherNo] = useState('');

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const handleVoucherInputChange = (e) => {
    setVoucherNo(e.target.value);
  };

  const handleScan = async () => {
    if (!voucherNo || !voucherNo.trim()) {
      setError('Please enter a voucher number');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const response = await MainService.MakePickupStatusInstore(voucherNo.trim());
      
      if (response === 'Success' || response === true || response === 'true') {
        setSuccess('Voucher scanned successfully. Order status updated to "In Store"');
        setVoucherNo('');
      } else if (response === 'Order Not Found') {
        setError('Order not found. Please check the voucher number.');
      } else if (response === 'Order Not In Pickup Scheduled Status') {
        setError('Order is not in "Pickup Scheduled" status. Current status does not allow this operation.');
      } else {
        setError(response || 'Failed to scan voucher');
      }
    } catch (err) {
      console.error('Scan voucher error:', err);
      setError('Network Error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleScan();
    }
  };

  const handleRefresh = () => {
    setVoucherNo('');
    setError('');
    setSuccess('');
  };

  return (
    <div className="space-y-6">
      {/* Toast Messages */}
      {error && (
        <div className="fixed top-4 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="fixed top-4 right-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{success}</span>
          <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Main Form Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="mb-6 pb-4 border-b border-gray-200">
          <div className="flex items-center mb-2">
            <ScanLine className="w-6 h-6 text-primary mr-2" />
            <h1 className="text-3xl font-bold text-gray-900">Pickup Scan</h1>
          </div>
          <p className="text-sm text-gray-500 ml-8">Scan voucher numbers for pickup</p>
        </div>

        <div className="space-y-6">
          <div className="grid grid-cols-12 gap-4 items-end">
            <div className="col-span-8">
              <label htmlFor="VoucherNo" className="block text-sm font-medium text-gray-700 mb-2">
                Voucher Number <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <ScanLine className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  id="VoucherNo"
                  name="VoucherNo"
                  value={voucherNo}
                  onChange={handleVoucherInputChange}
                  onKeyPress={handleKeyPress}
                  placeholder="Scan or enter voucher number"
                  autoFocus
                  className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-lg"
                />
              </div>
            </div>

            <div className="col-span-4 flex items-center gap-2">
              <button
                type="button"
                onClick={handleRefresh}
                disabled={loading}
                className="p-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Refresh"
              >
                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              </button>
              <button
                type="button"
                onClick={handleScan}
                disabled={loading || !voucherNo.trim()}
                className="flex-1 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-lg font-medium"
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-5 h-5 animate-spin" />
                    Scanning...
                  </>
                ) : (
                  <>
                    <ScanLine className="w-5 h-5" />
                    Scan
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Information Section - Flowchart */}
          <div className="mt-6 p-6 bg-gray-50 border border-gray-200 rounded-lg">
            <div className="mb-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-1">Process Flow:</h3>
              <p className="text-xs text-gray-600">This page is used when Drivers pick up orders from Traders</p>
            </div>
            
            {/* Flowchart with connecting lines */}
            <div className="relative">
              {/* Horizontal Flowchart for Desktop */}
              <div className="hidden md:flex items-center justify-center gap-0">
                {/* Step 1: Driver Picks Up */}
                <div className="relative flex flex-col items-center">
                  <div className="bg-white border-2 border-primary rounded-lg p-4 shadow-sm min-w-[140px] text-center relative z-10">
                    <Truck className="w-6 h-6 text-primary mx-auto mb-2" />
                    <p className="text-xs font-semibold text-gray-900">Driver Picks Up</p>
                    <p className="text-xs text-gray-600 mt-1">Orders from Traders</p>
                  </div>
                </div>

                {/* Connecting Line 1 */}
                <div className="flex-1 h-0.5 bg-primary relative mx-2">
                  <div className="absolute right-0 top-1/2 transform -translate-y-1/2">
                    <div className="w-0 h-0 border-l-8 border-l-primary border-t-4 border-t-transparent border-b-4 border-b-transparent"></div>
                  </div>
                </div>

                {/* Step 2: Return to Office */}
                <div className="relative flex flex-col items-center">
                  <div className="bg-white border-2 border-primary rounded-lg p-4 shadow-sm min-w-[140px] text-center relative z-10">
                    <Warehouse className="w-6 h-6 text-primary mx-auto mb-2" />
                    <p className="text-xs font-semibold text-gray-900">Returns to</p>
                    <p className="text-xs text-gray-600 mt-1">Office</p>
                  </div>
                </div>

                {/* Connecting Line 2 */}
                <div className="flex-1 h-0.5 bg-primary relative mx-2">
                  <div className="absolute right-0 top-1/2 transform -translate-y-1/2">
                    <div className="w-0 h-0 border-l-8 border-l-primary border-t-4 border-t-transparent border-b-4 border-b-transparent"></div>
                  </div>
                </div>

                {/* Step 3: Store Keeper Scans */}
                <div className="relative flex flex-col items-center">
                  <div className="bg-primary border-2 border-primary rounded-lg p-4 shadow-sm min-w-[140px] text-center relative z-10">
                    <ScanLine className="w-6 h-6 text-white mx-auto mb-2" />
                    <p className="text-xs font-semibold text-white">Store Keeper</p>
                    <p className="text-xs text-white opacity-90 mt-1">Scans Order</p>
                  </div>
                </div>

                {/* Connecting Line 3 */}
                <div className="flex-1 h-0.5 bg-primary relative mx-2">
                  <div className="absolute right-0 top-1/2 transform -translate-y-1/2">
                    <div className="w-0 h-0 border-l-8 border-l-primary border-t-4 border-t-transparent border-b-4 border-b-transparent"></div>
                  </div>
                </div>

                {/* Step 4: Status Changes */}
                <div className="relative flex flex-col items-center">
                  <div className="bg-white border-2 border-primary rounded-lg p-4 shadow-sm min-w-[140px] text-center relative z-10">
                    <CheckCircle className="w-6 h-6 text-primary mx-auto mb-2" />
                    <p className="text-xs font-semibold text-gray-900">Status Changes</p>
                    <p className="text-xs text-gray-600 mt-1">To "In Store"</p>
                  </div>
                </div>

                {/* Connecting Line 4 */}
                <div className="flex-1 h-0.5 bg-primary relative mx-2">
                  <div className="absolute right-0 top-1/2 transform -translate-y-1/2">
                    <div className="w-0 h-0 border-l-8 border-l-primary border-t-4 border-t-transparent border-b-4 border-b-transparent"></div>
                  </div>
                </div>

                {/* Step 5: Proceeding to Delivery */}
                <div className="relative flex flex-col items-center">
                  <div className="bg-white border-2 border-primary rounded-lg p-4 shadow-sm min-w-[140px] text-center relative z-10">
                    <Package className="w-6 h-6 text-primary mx-auto mb-2" />
                    <p className="text-xs font-semibold text-gray-900">Proceeding to</p>
                    <p className="text-xs text-gray-600 mt-1">Delivery</p>
                  </div>
                </div>
              </div>

              {/* Vertical Flowchart for Mobile */}
              <div className="md:hidden flex flex-col items-center gap-0">
                {/* Step 1: Driver Picks Up */}
                <div className="relative flex flex-col items-center w-full">
                  <div className="bg-white border-2 border-primary rounded-lg p-4 shadow-sm w-full max-w-[200px] text-center relative z-10">
                    <Truck className="w-6 h-6 text-primary mx-auto mb-2" />
                    <p className="text-xs font-semibold text-gray-900">Driver Picks Up</p>
                    <p className="text-xs text-gray-600 mt-1">Orders from Traders</p>
                  </div>
                </div>

                {/* Connecting Line 1 */}
                <div className="w-0.5 h-8 bg-primary relative my-2">
                  <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2">
                    <div className="w-0 h-0 border-t-8 border-t-primary border-l-4 border-l-transparent border-r-4 border-r-transparent"></div>
                  </div>
                </div>

                {/* Step 2: Return to Office */}
                <div className="relative flex flex-col items-center w-full">
                  <div className="bg-white border-2 border-primary rounded-lg p-4 shadow-sm w-full max-w-[200px] text-center relative z-10">
                    <Warehouse className="w-6 h-6 text-primary mx-auto mb-2" />
                    <p className="text-xs font-semibold text-gray-900">Returns to</p>
                    <p className="text-xs text-gray-600 mt-1">Office</p>
                  </div>
                </div>

                {/* Connecting Line 2 */}
                <div className="w-0.5 h-8 bg-primary relative my-2">
                  <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2">
                    <div className="w-0 h-0 border-t-8 border-t-primary border-l-4 border-l-transparent border-r-4 border-r-transparent"></div>
                  </div>
                </div>

                {/* Step 3: Store Keeper Scans */}
                <div className="relative flex flex-col items-center w-full">
                  <div className="bg-primary border-2 border-primary rounded-lg p-4 shadow-sm w-full max-w-[200px] text-center relative z-10">
                    <ScanLine className="w-6 h-6 text-white mx-auto mb-2" />
                    <p className="text-xs font-semibold text-white">Store Keeper</p>
                    <p className="text-xs text-white opacity-90 mt-1">Scans Order</p>
                  </div>
                </div>

                {/* Connecting Line 3 */}
                <div className="w-0.5 h-8 bg-primary relative my-2">
                  <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2">
                    <div className="w-0 h-0 border-t-8 border-t-primary border-l-4 border-l-transparent border-r-4 border-r-transparent"></div>
                  </div>
                </div>

                {/* Step 4: Status Changes */}
                <div className="relative flex flex-col items-center w-full">
                  <div className="bg-white border-2 border-primary rounded-lg p-4 shadow-sm w-full max-w-[200px] text-center relative z-10">
                    <CheckCircle className="w-6 h-6 text-primary mx-auto mb-2" />
                    <p className="text-xs font-semibold text-gray-900">Status Changes</p>
                    <p className="text-xs text-gray-600 mt-1">To "In Store"</p>
                  </div>
                </div>

                {/* Connecting Line 4 */}
                <div className="w-0.5 h-8 bg-primary relative my-2">
                  <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2">
                    <div className="w-0 h-0 border-t-8 border-t-primary border-l-4 border-l-transparent border-r-4 border-r-transparent"></div>
                  </div>
                </div>

                {/* Step 5: Proceeding to Delivery */}
                <div className="relative flex flex-col items-center w-full">
                  <div className="bg-white border-2 border-primary rounded-lg p-4 shadow-sm w-full max-w-[200px] text-center relative z-10">
                    <Package className="w-6 h-6 text-primary mx-auto mb-2" />
                    <p className="text-xs font-semibold text-gray-900">Proceeding to</p>
                    <p className="text-xs text-gray-600 mt-1">Delivery</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PickupScan;

